import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ImageMaskProps } from './ImageMask.types';
import { useTranslation } from 'react-i18next';

const CANVAS_SIZE = 768;

// ---- Rect/ellipse settings ----
const FILL_RGBA = 'rgba(63,163,243,0.4)'; // 40%
const STROKE_HEX = '#3FA3F3';
const DASH_PATTERN: [number, number] = [8, 6];
const RECT_RADIUS = 8;

// ---- Brush settings ----
const BRUSH_WIDTH = 28;
const BRUSH_ALPHA = 0.1;

// Helpers
function drawRoundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.max(0, Math.min(r, Math.min(w, h) / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
}

function drawEllipsePath(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
) {
  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const w = Math.abs(x2 - x1);
  const h = Math.abs(y2 - y1);
  const cx = left + w / 2;
  const cy = top + h / 2;
  const rx = w / 2;
  const ry = h / 2;

  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
}

function ImageMask({ tool, onMaskChange, ref }: ImageMaskProps) {
  const { t } = useTranslation('contentTest');

  const holderRef = useRef<HTMLDivElement | null>(null);
  const maskRef = useRef<HTMLCanvasElement | null>(null);
  const previewRef = useRef<HTMLCanvasElement | null>(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const lastBrushPos = useRef<{ x: number; y: number } | null>(null);

  useImperativeHandle(ref, () => ({
    clear: () => {
      const m = maskRef.current?.getContext('2d');
      const p = previewRef.current?.getContext('2d');
      if (m) m.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      if (p) p.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

      if (typeof onMaskChange === 'function') onMaskChange('');
    },
    getPNGDataURL: () => maskRef.current?.toDataURL('image/png') ?? '',
    getBase64: () => {
      const url = maskRef.current?.toDataURL('image/png') ?? '';
      const idx = url.indexOf(',');
      return idx >= 0 ? url.slice(idx + 1) : '';
    },
    getPNGBlob: () =>
      new Promise<Blob>((resolve) => {
        maskRef.current?.toBlob((b) => resolve(b ?? new Blob()), 'image/png');
      }),
  }));

  useEffect(() => {
    const m = maskRef.current?.getContext('2d');
    const p = previewRef.current?.getContext('2d');
    if (m) m.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    if (p) p.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    if (typeof onMaskChange === 'function') onMaskChange('');
  }, [onMaskChange]);

  const getCoords = (e: React.MouseEvent | React.TouchEvent) => {
    const rect = holderRef.current!.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const x = ((clientX - rect.left) / rect.width) * CANVAS_SIZE;
    const y = ((clientY - rect.top) / rect.height) * CANVAS_SIZE;
    return { x, y };
  };

  const begin = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    const { x, y } = getCoords(e);
    startPos.current = { x, y };

    if (tool === 'brush') {
      const mctx = maskRef.current!.getContext('2d')!;
      mctx.beginPath();
      mctx.moveTo(x, y);
      lastBrushPos.current = { x, y };
    }
  };

  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoords(e);
    const s = startPos.current!;

    if (tool === 'brush') {
      const mctx = maskRef.current!.getContext('2d')!;
      mctx.save();
      mctx.globalAlpha = BRUSH_ALPHA;
      mctx.lineCap = 'round';
      mctx.lineJoin = 'round';
      mctx.lineWidth = BRUSH_WIDTH;
      mctx.strokeStyle = STROKE_HEX;
      mctx.lineTo(x, y);
      mctx.stroke();
      mctx.restore();

      const lp = lastBrushPos.current;
      if (lp) {
        const pctx = previewRef.current!.getContext('2d')!;
        pctx.setLineDash(DASH_PATTERN);
        pctx.lineDashOffset = 0;
        pctx.lineCap = 'round';
        pctx.lineJoin = 'round';
        pctx.strokeStyle = STROKE_HEX;
        pctx.lineWidth = 2;
        pctx.beginPath();
        pctx.moveTo(lp.x, lp.y);
        pctx.lineTo(x, y);
        pctx.stroke();
      }
      lastBrushPos.current = { x, y };
    } else {
      const pctx = previewRef.current!.getContext('2d')!;
      pctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
      pctx.fillStyle = FILL_RGBA;

      if (tool === 'rect') {
        const left = Math.min(s.x, x);
        const top = Math.min(s.y, y);
        const w = Math.abs(x - s.x);
        const h = Math.abs(y - s.y);

        drawRoundedRectPath(pctx, left, top, w, h, RECT_RADIUS);
        pctx.fill();

        pctx.setLineDash(DASH_PATTERN);
        pctx.strokeStyle = STROKE_HEX;
        pctx.lineWidth = 2;
        drawRoundedRectPath(
          pctx,
          left + 1,
          top + 1,
          Math.max(0, w - 2),
          Math.max(0, h - 2),
          RECT_RADIUS,
        );
        pctx.stroke();
      } else {
        drawEllipsePath(pctx, s.x, s.y, x, y);
        pctx.fill();

        pctx.setLineDash(DASH_PATTERN);
        pctx.strokeStyle = STROKE_HEX;
        pctx.lineWidth = 2;

        const left = Math.min(s.x, x);
        const top = Math.min(s.y, y);
        const w = Math.abs(x - s.x);
        const h = Math.abs(y - s.y);
        drawEllipsePath(
          pctx,
          left + 1,
          top + 1,
          left + Math.max(0, w - 2),
          top + Math.max(0, h - 2),
        );
        pctx.stroke();
      }
    }
  };

  const end = () => {
    if (!isDrawing) return;
    setIsDrawing(false);

    if (tool !== 'brush' && startPos.current) {
      const mctx = maskRef.current!.getContext('2d')!;
      mctx.drawImage(previewRef.current!, 0, 0);
    }

    const pctx = previewRef.current!.getContext('2d')!;
    pctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
    pctx.setLineDash([]);

    startPos.current = null;
    lastBrushPos.current = null;

    if (typeof onMaskChange === 'function') {
      const url = maskRef.current?.toDataURL('image/png') ?? '';
      const idx = url.indexOf(',');
      const b64 = idx >= 0 ? url.slice(idx + 1) : '';
      onMaskChange(b64);
    }
  };

  return (
    <div
      ref={holderRef}
      role="presentation"
      aria-label={t('imageWidget.imageMask.imageEditMask')}
      className="absolute inset-0"
      style={{ cursor: 'crosshair' }}
      onMouseDown={begin}
      onMouseMove={move}
      onMouseUp={end}
      onMouseLeave={end}
      onTouchStart={begin}
      onTouchMove={move}
      onTouchEnd={end}
    >
      <canvas
        ref={maskRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: 'none' }}
      />
      <canvas
        ref={previewRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        className="absolute inset-0 w-full h-full"
        style={{ pointerEvents: 'none' }}
      />
    </div>
  );
}

ImageMask.displayName = 'ImageMask';
export default ImageMask;
